import React, { useEffect, useState } from "react";
import { Modal, Input, Table, Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { http } from "../../services/http";
import moment from "moment";

const ViewMaterialReports = ({ visible, data, onClose, filters }) => {
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(30);
  const [hasMore, setHasMore] = useState(false);
  const [itemCode, setItemCode] = useState("");
  const [resData, setResData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);

  const fetchData = async (startDate, endDate, currentPage, itemCode) => {
    setLoading(true);
    try {
      let url = `api/dashboard/movement-of-goods-report?startDate=${startDate}&endDate=${endDate}&itemCode=${itemCode}&skip=${currentPage}&pageSize=${pageSize}`;

      const { data } = await http.get(url);

      setHasMore(data.length === pageSize);

      setResData(data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setExcelLoading(true);
    let backUrl = `api/dashboard/movement-of-goods-report/excel`;
    const body = {
      startDate,
      endDate,
      itemCode,
      skip: 0,
      pageSize: 10000,
    };

    try {
      const res = await http.post(backUrl, body, {
        headers: {
          "Content-Type": "application/json",
        },
        responseType: "blob", // Ensure the response is treated as a blob
      });

      const url = window.URL.createObjectURL(
        new Blob([res.data], {
          type: "application/json",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        "Maxsulotlar bo'yicha harakatlar hisoboti.xlsx",
      ); // Define the download file name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {}
    setExcelLoading(false);
  };

  const columns = [
    {
      title: t("itemName"),
      dataIndex: "itemName",
      key: "itemName",
    },
    {
      title: t("invntryUom"),
      dataIndex: "inventoryUom",
      key: "inventoryUom",
    },
    {
      title: t("docNum"),
      dataIndex: "transNum",
      key: "transNum",
    },
    {
      title: t("docDate"),
      dataIndex: "docDate",
      key: "docDate",
      render: (text) => {
        return moment(text).format("DD.MM.YYYY");
      },
    },
    {
      title: t("docType"),
      dataIndex: "docType",
      key: "docType",
    },
    {
      title: t("whs"),
      dataIndex: "whsName",
      key: "whsName",
    },

    // {
    //   title: t("fromWhs"),
    //   dataIndex: "fromWhsName",
    //   key: "fromWhsName",
    // },
    // {
    //   title: t("toWhs"),
    //   dataIndex: "toWhsName",
    //   key: "toWhsName",
    // },
    {
      title: t("income"),
      dataIndex: "income",
      key: "income",
      render: (text, record) => {
        const formattedText = new Intl.NumberFormat("fr-FR").format(text);
        return `${formattedText} `;
      },
    },
    {
      title: t("outcome"),
      dataIndex: "outcome",
      key: "outcome",
      render: (text, record) => {
        const formattedText = new Intl.NumberFormat("fr-FR").format(text);
        return `${formattedText} `;
      },
    },
  ];

  useEffect(() => {
    if (data) {
      setItemCode(data.itemCode);
    }
    if (filters) {
      setStartDate(filters.startDate);
      setEndDate(filters.endDate);
    }
  }, [data, visible, filters]);

  useEffect(() => {
    if (itemCode && visible) {
      fetchData(startDate, endDate, currentPage, itemCode);
    }
  }, [startDate, endDate, currentPage, itemCode, visible]);

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const handleNextPage = () => {
    if (hasMore) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const onCloseModal = () => {
    onClose();
    setStartDate("");
    setEndDate("");
    setCurrentPage(0);
    setHasMore(false);
    setResData([]);
  };

  if (!data) return null;

  return (
    <Modal
      title={
        <h1 className="font-nunito text-sm font-extrabold text-[#000000]">
          {t("movementReportsByItem")} : {data.itemCode}
        </h1>
      }
      visible={visible}
      onCancel={onCloseModal}
      footer={null}
      closeIcon={<CloseOutlined />}
      width="95%"
      bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
    >
      <div className="w-full px-4">
        <div className="mt-10 flex items-end justify-between">
          <div className="flex items-start gap-4">
            <div className="flex flex-col gap-3">
              <p>{t("startDate")}</p>
              <Input
                type="date"
                value={startDate}
                className="w-[150px]"
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate}
              />
            </div>
            <div className="flex flex-col gap-3">
              <p>{t("endDate")}</p>
              <Input
                type="date"
                value={endDate}
                className="w-[150px]"
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                max={moment().format("YYYY-MM-DD")}
              />
            </div>
          </div>
          <div className="flex flex-col items-center">
            <div className="font-nunitto font-bold">
              {t("page")} : {currentPage + 1}
            </div>
            <div className="mt-2 flex gap-2">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 0}
                className="h-[40px] w-[120px] rounded-2xl bg-gray-300 text-gray-700 disabled:bg-gray-200 disabled:text-gray-400 sm:w-[120px]"
              >
                {t("previous")}
              </button>
              <button
                onClick={handleNextPage}
                disabled={!hasMore}
                className="h-[40px] w-[120px] rounded-2xl bg-[#0A4D68] text-white disabled:bg-gray-200 disabled:text-gray-400 sm:w-[120px]"
              >
                {t("next")}
              </button>
            </div>
          </div>
        </div>
        <div className="mt-10 flex items-center justify-end px-4">
          <Button
            onClick={handleExportExcel}
            loading={excelLoading}
            type="primary"
            className="ml-5 h-10 w-full rounded-lg bg-green-700 text-white sm:w-[200px]"
          >
            {t("exportExcel")}
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={resData}
          pagination={false}
          loading={loading}
          className="border-1 mt-5 w-full border-black"
          scroll={{ x: "100%" }}
        />
      </div>
    </Modal>
  );
};

export default ViewMaterialReports;
